/**
 * useProvisioningSSE.ts
 * Feature: 155 - Telemetry Pipeline Provisioning & Management
 * Author: Peter Dobler (Dobler Data Solutions)
 * Date: 2026-05-19
 * Constitution Version: v1.12.0 | Principle XXXVII (Code Header Standards)
 *
 * React hook for consuming Server-Sent Events from a POST endpoint.
 * Uses fetch + ReadableStream because the provisioning endpoint is a POST
 * (EventSource only supports GET).
 */

import { useState, useCallback, useRef } from 'react';
import { getAuthHeader } from '../services/apiClient';
import { isDevAuthBypass } from '../authConfig';
import { getDevUserRole } from '../auth/devUserStore';
import type { SseDoneEvent, SseProgressEvent } from '@shared/types/telemetryPipeline';

export type { SseDoneEvent, SseProgressEvent };

export interface UseProvisioningSSEResult {
  logLines: string[];
  doneEvent: SseDoneEvent | null;
  isStreaming: boolean;
  error: string | null;
  start: (body: object) => void;
  reset: () => void;
}

export function useProvisioningSSE(endpoint: string): UseProvisioningSSEResult {
  const [logLines, setLogLines] = useState<string[]>([]);
  const [doneEvent, setDoneEvent] = useState<SseDoneEvent | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);

  const reset = useCallback(() => {
    setLogLines([]);
    setDoneEvent(null);
    setIsStreaming(false);
    setError(null);
  }, []);

  const start = useCallback(
    async (body: object) => {
      reset();
      setIsStreaming(true);

      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (isDevAuthBypass) {
          const devRole = getDevUserRole();
          if (devRole) headers['X-Dev-User-Role'] = devRole;
        } else {
          const authHeader = await getAuthHeader();
          if (authHeader) headers['Authorization'] = authHeader;
        }

        const response = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          const errText = await response.text();
          let errMsg = `Request failed (${response.status})`;
          try {
            const parsed = JSON.parse(errText) as { error?: string };
            errMsg = parsed.error ?? errMsg;
          } catch { /* ignore parse failure */ }
          setError(errMsg);
          setIsStreaming(false);
          return;
        }

        if (!response.body) {
          setError('No response body — server did not stream SSE');
          setIsStreaming(false);
          return;
        }

        const reader = response.body.getReader();
        readerRef.current = reader;
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const blocks = buffer.split('\n\n');
          buffer = blocks.pop() ?? '';

          for (const block of blocks) {
            const dataLine = block
              .split('\n')
              .find((l) => l.startsWith('data: '));
            if (!dataLine) continue;

            const raw = dataLine.slice('data: '.length);
            let event: SseProgressEvent | SseDoneEvent;
            try {
              event = JSON.parse(raw) as SseProgressEvent | SseDoneEvent;
            } catch {
              setLogLines((prev) => [...prev, raw]);
              continue;
            }

            if (event.type === 'progress') {
              setLogLines((prev) => [...prev, event.message]);
            } else if (event.type === 'done') {
              setDoneEvent(event);
              setIsStreaming(false);
            }
          }
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
      } finally {
        setIsStreaming(false);
        readerRef.current = null;
      }
    },
    [endpoint, reset],
  );

  return { logLines, doneEvent, isStreaming, error, start, reset };
}
